import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2, Phone, Mail, MessageCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useSubmitLock } from "@/hooks/use-submit-lock";
import { getUtmData } from "@/hooks/use-utm";
import { analyticsVisitorFields } from "@/lib/visitor-link";
import { trackConversion } from "@/lib/analytics";
import { Link } from "wouter";
import { CountryCombobox } from "@/components/country-combobox";
import { PhoneInputField } from "@/components/phone-input";
import { isValidPhoneE164 } from "@/lib/phone";
import { knownAccountEmail } from "@shared/account-email";

type ContactPreference = "email" | "phone" | "whatsapp";

interface FormState {
  name: string;
  email: string;
  phone: string;
  countryOfBirth: string;
  contactPreference: ContactPreference;
}

interface InvestorPackageModalProps {
  open: boolean;
  onClose: () => void;
  projectId?: string | null;
  /** Signed-in account email. When set, the form does not ask for it again. */
  accountEmail?: string | null;
}

export function InvestorPackageModal({
  open,
  onClose,
  projectId,
  accountEmail: accountEmailProp,
}: InvestorPackageModalProps) {
  const accountEmail = knownAccountEmail(accountEmailProp);
  const [submitted, setSubmitted] = useState(false);
  const submitLock = useSubmitLock(500);

  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    countryOfBirth: "",
    contactPreference: "email",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const utm = getUtmData();
      await apiRequest("POST", "/api/inquiries", {
        name: form.name,
        email: accountEmail ?? form.email,
        phone: form.phone || "",
        country: form.countryOfBirth,
        message: `Contact preference: ${form.contactPreference}`,
        source: "homepage_investor_package",
        projectId: projectId ?? null,
        ...utm,
        ...analyticsVisitorFields(),
      });

      trackConversion("investor_package_requested", {
        source: "homepage_investor_package",
        projectId: projectId ?? undefined,
        value: 800000,
        currency: "USD",
        ...utm,
      });
    },
    onSuccess: () => setSubmitted(true),
  });

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = "Full name is required.";
    if (!accountEmail) {
      if (!form.email.trim()) {
        next.email = "Email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        next.email = "Please enter a valid email address.";
      }
    }
    if (form.phone && !isValidPhoneE164(form.phone)) {
      next.phone = "Please enter a valid phone number.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    if (mutation.isPending || submitLock.locked) return;
    void submitLock.run(() => mutation.mutateAsync());
  }

  function handleClose() {
    if (mutation.isPending) return;
    onClose();
    // Reset form after close animation finishes
    setTimeout(() => {
      setSubmitted(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        countryOfBirth: "",
        contactPreference: "email",
      });
      setErrors({});
    }, 300);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="modal-investor-package"
      >
        {submitted ? (
          <div className="flex flex-col items-center text-center py-6 gap-4" data-testid="investor-package-success">
            <CheckCircle className="w-14 h-14 text-emerald-500" />
            <DialogHeader>
              <DialogTitle className="text-xl">Thank you.</DialogTitle>
              <DialogDescription className="text-base text-foreground">
                A member of our team will reach out within one business day.
              </DialogDescription>
            </DialogHeader>
            <Link href="/schedule-call" onClick={handleClose}>
              <Button
                variant="outline"
                className="mt-2"
                data-testid="button-book-call-after-package"
              >
                Or book a call now
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle data-testid="modal-package-title">Request the Investor Package</DialogTitle>
              <DialogDescription>
                Get the full American Energy EB-5 offering summary, project details, and next steps.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              {/* Full Name */}
              <div className="space-y-1.5">
                <Label htmlFor="pkg-name">Full Name <span className="text-destructive">*</span></Label>
                <Input
                  id="pkg-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full legal name"
                  data-testid="input-package-name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p className="text-xs text-destructive" data-testid="error-package-name">{errors.name}</p>
                )}
              </div>

              {accountEmail ? null : (
              <div className="space-y-1.5">
                <Label htmlFor="pkg-email">Email <span className="text-destructive">*</span></Label>
                <Input
                  id="pkg-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  data-testid="input-package-email"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-xs text-destructive" data-testid="error-package-email">{errors.email}</p>
                )}
              </div>
              )}

              {/* Phone */}
              <div className="space-y-1.5">
                <Label>Phone (optional)</Label>
                <PhoneInputField
                  value={form.phone}
                  onChange={(val) => setForm({ ...form, phone: val })}
                  defaultCountry={form.countryOfBirth}
                  error={errors.phone}
                  data-testid="input-package-phone"
                />
              </div>

              {/* Country of Birth */}
              <div className="space-y-1.5">
                <Label>Country of Birth</Label>
                <CountryCombobox
                  value={form.countryOfBirth}
                  onValueChange={(v) => setForm({ ...form, countryOfBirth: v })}
                />
              </div>

              {/* Contact preference */}
              <div className="space-y-2">
                <Label>How do you prefer to be contacted?</Label>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Contact preference">
                  {(
                    [
                      { value: "email", label: "Email", icon: Mail },
                      { value: "phone", label: "Phone", icon: Phone },
                      { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                    ] as { value: ContactPreference; label: string; icon: typeof Mail }[]
                  ).map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={form.contactPreference === value}
                      onClick={() => setForm({ ...form, contactPreference: value })}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                        form.contactPreference === value
                          ? "bg-primary text-white border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                      data-testid={`radio-contact-${value}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={mutation.isPending || submitLock.locked}
                className="btn-gold-3d text-brand-dark-bg font-semibold w-full"
                data-testid="button-submit-investor-package"
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  "Request the Investor Package"
                )}
              </Button>

              {mutation.isError && (
                <p className="text-xs text-destructive text-center" data-testid="error-package-submit">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}

              <p className="text-xs text-muted-foreground text-center leading-relaxed" data-testid="text-package-consent">
                By submitting this form, you agree to be contacted about EB-5 programs. Your information is kept confidential and is never sold.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
